import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Search, 
  Filter, 
  ChevronRight, 
  Clock, 
  CheckCircle2, 
  Package, 
  RefreshCcw, 
  AlertCircle,
  TrendingUp,
  CreditCard,
  Calendar,
  User,
  ShoppingBag,
  MoreVertical,
  Eye,
  Check,
  X as XIcon,
  Download,
  Receipt
} from 'lucide-react';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  updateDoc, 
  doc,
  orderBy,
  Timestamp,
  increment
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useRealtime } from '../../contexts/RealtimeContext';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const OrderManagement = ({ coachingId }) => {
  const { userProfile } = useRealtime();

  
  // Permission check for staff
  const isStaff = userProfile?.role === 'coaching_staff';
  const hasEditAccess = !isStaff || userProfile?.accessibleSections?.includes('store');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [summary, setSummary] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0
  });

  const orderStatuses = [
    { id: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30' },
    { id: 'progress', label: 'In Progress', icon: RefreshCcw, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30' },
    { id: 'completed', label: 'Completed', icon: CheckCircle2, color: 'text-green-600 bg-green-100 dark:bg-green-900/30' },
    { id: 'returned', label: 'Returned', icon: AlertCircle, color: 'text-red-600 bg-red-100 dark:bg-red-900/30' }
  ];

  useEffect(() => {
    loadOrders();
  }, [coachingId]);

  const loadOrders = async () => {
    if (!coachingId) return;
    try {
      setLoading(true);
      const ordersRef = collection(db, COLLECTIONS.STORE_ORDERS || 'storeOrders');
      
      let ordersData = [];
      try {
        // Try optimized query with orderBy (requires composite index)
        const q = query(
          ordersRef, 
          where('coachingId', '==', coachingId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
      } catch (indexError) {
        console.warn('OrderManagement: Missing index for orders query, falling back to client-side sort:', indexError);
        // Fallback: Simple query without orderBy
        const fallbackQ = query(
          ordersRef, 
          where('coachingId', '==', coachingId)
        );
        const snapshot = await getDocs(fallbackQ);
        ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        // Sort in memory
        ordersData.sort((a, b) => b.createdAt - a.createdAt);
      }
      
      setOrders(ordersData);
      calculateSummary(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (ordersData) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const currentMonthOrders = ordersData.filter(order => order.createdAt >= startOfMonth);
    
    const stats = {
      totalOrders: currentMonthOrders.length,
      totalRevenue: currentMonthOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0),
      pendingOrders: ordersData.filter(o => o.status === 'pending').length,
      completedOrders: ordersData.filter(o => o.status === 'completed').length
    };
    
    setSummary(stats);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) return;

      const orderRef = doc(db, COLLECTIONS.STORE_ORDERS || 'storeOrders', orderId);
      
      // Stock adjustment logic
      if (newStatus === 'progress' && !order.stockAdjusted) {
        // Accept: Deduct stock
        for (const item of order.items || []) {
          if (item.id) {
            const itemRef = doc(db, COLLECTIONS.STORE_ITEMS || 'storeItems', item.id);
            const itemSnap = await getDoc(itemRef);
            if (itemSnap.exists()) {
              const currentStock = itemSnap.data().quantityInStock || 0;
              const newStock = Math.max(0, currentStock - item.quantity);
              await updateDoc(itemRef, {
                quantityInStock: newStock,
                inStock: newStock > 0
              });
            }
          }
        }
        await updateDoc(orderRef, { stockAdjusted: true });
      } else if (newStatus === 'returned' && order.stockAdjusted) {
        // Returned/Rejected: Add back stock if it was previously deducted
        for (const item of order.items || []) {
          if (item.id) {
            const itemRef = doc(db, COLLECTIONS.STORE_ITEMS || 'storeItems', item.id);
            const itemSnap = await getDoc(itemRef);
            if (itemSnap.exists()) {
              const currentStock = itemSnap.data().quantityInStock || 0;
              const newStock = currentStock + item.quantity;
              await updateDoc(itemRef, {
                quantityInStock: newStock,
                inStock: newStock > 0
              });
            }
          }
        }
        await updateDoc(orderRef, { stockAdjusted: false });
      }

      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });
      
      const statusLabels = {
        'progress': 'accepted and is now in progress',
        'returned': 'rejected',
        'completed': 'marked as complete'
      };
      
      toast.success(`Order ${statusLabels[newStatus] || newStatus}`);
      loadOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update status');
    }
  };

  const generateReceipt = (order) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const institute = userProfile?.reportCardInstituteDefaults || {};
      const instituteName = userProfile?.coachingName || userProfile?.institutionName || 'Coaching Institute';

      // Header - Institute Info
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(instituteName, pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 116, 139);
      
      let y = 28;
      if (institute.affiliationNumber) {
        doc.text(`Affiliation No.: ${institute.affiliationNumber}`, pageWidth / 2, y, { align: 'center' });
        y += 5;
      }
      if (institute.address) {
        const addressLines = doc.splitTextToSize(institute.address, pageWidth - 40);
        addressLines.forEach(line => {
          doc.text(line, pageWidth / 2, y, { align: 'center' });
          y += 5;
        });
      }
      const contactLine = [institute.instituteEmail, institute.institutePhone].filter(Boolean).join('  |  ');
      if (contactLine) {
        doc.text(contactLine, pageWidth / 2, y, { align: 'center' });
        y += 5;
      }

      // Divider
      doc.setDrawColor(226, 232, 240);
      doc.line(20, y + 5, pageWidth - 20, y + 5);
      y += 15;

      // Receipt Title & Order Info
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('ORDER RECEIPT', 20, y);
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Order ID: #${order.id.slice(-8).toUpperCase()}`, pageWidth - 20, y, { align: 'right' });
      y += 10;

      doc.text(`Date: ${order.createdAt.toLocaleDateString()}`, 20, y);
      doc.text(`Status: ${order.status.toUpperCase()}`, pageWidth - 20, y, { align: 'right' });
      y += 15;

      // Student Info
      doc.setFont(undefined, 'bold');
      doc.text('Bill To:', 20, y);
      y += 5;
      doc.setFont(undefined, 'normal');
      doc.text(order.studentName || 'Student Name', 20, y);
      y += 5;
      doc.text(order.studentEmail || '', 20, y);
      y += 15;

      // Items Table
      const tableData = order.items.map(item => [
        item.name + (item.size ? ` (${item.size})` : ''),
        item.quantity,
        `₹${item.price.toLocaleString()}`,
        `₹${(item.price * item.quantity).toLocaleString()}`
      ]);

      doc.autoTable({
        startY: y,
        head: [['Item Description', 'Qty', 'Price', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        foot: [['', '', 'Grand Total:', `₹${order.totalAmount.toLocaleString()}`]],
        footStyles: { fillColor: [248, 250, 252], textColor: [30, 41, 59], fontStyle: 'bold' }
      });

      // Footer
      const finalY = doc.lastAutoTable.finalY + 30;
      doc.setFontSize(10);
      doc.text('Authorized Signatory', pageWidth - 20, finalY, { align: 'right' });
      doc.line(pageWidth - 60, finalY - 5, pageWidth - 20, finalY - 5);

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('This is a computer generated receipt.', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

      doc.save(`Receipt-${order.id.slice(-6).toUpperCase()}.pdf`);
      toast.success('Receipt generated successfully');
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items?.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600">
              <ShoppingBag size={20} />
            </div>
            <span className="text-xs font-medium text-gray-500">This Month</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{summary.totalOrders}</h3>
          <p className="text-sm text-gray-500 mt-1">Total Orders</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-600">
              <CreditCard size={20} />
            </div>
            <span className="text-xs font-medium text-gray-500">This Month</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">₹{summary.totalRevenue.toLocaleString()}</h3>
          <p className="text-sm text-gray-500 mt-1">Payment Collected</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg text-yellow-600">
              <Clock size={20} />
            </div>
            <span className="text-xs font-medium text-gray-500">Total</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{summary.pendingOrders}</h3>
          <p className="text-sm text-gray-500 mt-1">Pending Orders</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600">
              <CheckCircle2 size={20} />
            </div>
            <span className="text-xs font-medium text-gray-500">Total</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{summary.completedOrders}</h3>
          <p className="text-sm text-gray-500 mt-1">Completed Orders</p>
        </motion.div>
      </div>

      {/* Controls Section */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by student name, order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 w-full md:w-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              All Orders
            </button>
            {orderStatuses.map(status => (
              <button
                key={status.id}
                onClick={() => setStatusFilter(status.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  statusFilter === status.id 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Order Details</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Student</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Items & Qty</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const statusInfo = orderStatuses.find(s => s.id === order.status) || orderStatuses[0];
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-900 dark:text-white">#{order.id.slice(-6).toUpperCase()}</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <Calendar size={12} /> {order.createdAt.toLocaleDateString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500">
                            <User size={16} />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{order.studentName}</span>
                            <span className="text-xs text-gray-500">{order.studentEmail}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-1.5">
                              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-[10px] font-bold">
                                {item.quantity}
                              </span>
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {item.name} {item.size ? `(${item.size})` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-gray-900 dark:text-white">₹{order.totalAmount?.toLocaleString()}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          <StatusIcon size={12} />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {hasEditAccess ? (
                            <>
                              <AnimatePresence mode="wait">
                                {order.status === 'pending' && (
                                  <motion.div 
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    className="flex gap-2"
                                  >
                                    <button
                                      onClick={() => handleStatusUpdate(order.id, 'progress')}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg transition-colors"
                                      title="Accept Order"
                                    >
                                      <Check size={14} /> Accept
                                    </button>
                                    <button
                                      onClick={() => handleStatusUpdate(order.id, 'returned')}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors"
                                      title="Reject Order"
                                    >
                                      <XIcon size={14} /> Reject
                                    </button>
                                  </motion.div>
                                )}

                                {order.status === 'progress' && (
                                  <motion.div 
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                  >
                                    <button
                                      onClick={() => handleStatusUpdate(order.id, 'completed')}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors"
                                    >
                                      <CheckCircle2 size={14} /> Mark Complete
                                    </button>
                                  </motion.div>
                                )}

                                {order.status === 'completed' && (
                                  <motion.div 
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                  >
                                    <button
                                      onClick={() => generateReceipt(order)}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors"
                                    >
                                      <Receipt size={14} /> Receipt
                                    </button>
                                  </motion.div>
                                )}

                                {order.status === 'returned' && (
                                  <motion.div 
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                  >
                                    <span className="text-xs text-gray-400 italic">No actions</span>
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              <div className="ml-2 pt-1">
                                <select
                                  value={order.status}
                                  onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                                  className="text-[10px] bg-transparent border-none text-gray-400 hover:text-gray-600 outline-none cursor-pointer"
                                >
                                  <option value="" disabled>Change Status</option>
                                  {orderStatuses.map(s => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          ) : (
                            order.status === 'completed' ? (
                              <button
                                onClick={() => generateReceipt(order)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors"
                              >
                                <Receipt size={14} /> Receipt
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400 italic">View Only</span>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No orders found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;
